import React, { useMemo } from "react";
import dayjs from "dayjs";
import { useGetStaffAvailabilities } from "~/services/staff/staff-availability.queries";
import { useGetServiceTickets } from "~/services/service-ticket";
import { useGetStaffRoleByName } from "~/services/common";
import { useGetExpenseStats } from "~/services/expense";
import { StaffAvailability, ServiceType } from "~/utils/constants";

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Calculate hours from timeIn/timeOut or duration
const calculateHours = (ticket: any): number => {
  if (ticket.timeIn && ticket.timeOut) {
    const timeIn = dayjs(ticket.timeIn);
    const timeOut = dayjs(ticket.timeOut);
    return Math.max(0, timeOut.diff(timeIn, "hour", true));
  }
  return Number(ticket.duration) || 1; // Default 1 hour if no data
};

// Calculate service price using staff rates and vessel rates
const calculateServicePrice = (ticket: any, defaultStaffRate: number): number => {
  const hours = calculateHours(ticket);
  const staffCount = ticket.staffs?.length || 1;
  const staffRate = defaultStaffRate;
  const vesselRate = ticket.vessel?.hourlyRate || 0;

  // Staff cost + Vessel cost
  const staffCost = staffCount * staffRate * hours;
  const vesselCost = vesselRate * hours;

  return staffCost + vesselCost;
};

export const Metrics: React.FC = () => {
  const today = dayjs().format("YYYY-MM-DD");

  // Staff availability for today
  const { data: staffAvailabilityData } = useGetStaffAvailabilities({
    startDate: today,
    endDate: today,
  });

  // Service tickets (use full query to get vessel and staff data)
  const { data: serviceTicketsData } = useGetServiceTickets({
    startDate: today,
    endDate: today,
    pageSize: 100,
  });

  // Get Art Handler staff role for hourly rate
  const { data: artHandlerRole } = useGetStaffRoleByName("Art Handler");

  // Expense stats
  const { data: expenseStats } = useGetExpenseStats();

  // Art Handler hourly rate (default to $100 if not found)
  const artHandlerRate = artHandlerRole?.hourlyRate || 100;

  // Calculate staff available today (FULL_TIME or PART_TIME)
  const staffAvailable = useMemo(() => {
    if (!staffAvailabilityData?.data) return 0;
    return staffAvailabilityData.data.filter(
      (schedule) =>
        schedule.availability === StaffAvailability.FULL_TIME ||
        schedule.availability === StaffAvailability.PART_TIME
    ).length;
  }, [staffAvailabilityData]);

  // Calculate service type metrics with pricing
  const serviceMetrics = useMemo(() => {
    const tickets = serviceTicketsData?.data || [];

    const transport = { count: 0, revenue: 0 };
    const site = { count: 0, revenue: 0 };
    const warehouse = { count: 0, revenue: 0 };
    let totalHours = 0;

    tickets.forEach((ticket) => {
      const hours = calculateHours(ticket);
      totalHours += hours;

      const price = calculateServicePrice(ticket, artHandlerRate);

      switch (ticket.serviceType) {
        case ServiceType.COLLECTION:
        case ServiceType.DELIVERY:
          transport.count++;
          transport.revenue += price;
          break;
        case ServiceType.ON_SITE_SERVICE:
        case ServiceType.FABRICATION_SERVICE:
          site.count++;
          site.revenue += price;
          break;
        case ServiceType.WAREHOUSE_SERVICE:
          warehouse.count++;
          warehouse.revenue += price;
          break;
      }
    });

    return { transport, site, warehouse, totalHours, totalServices: tickets.length };
  }, [serviceTicketsData, artHandlerRate]);

  // Financial metrics
  const financialMetrics = useMemo(() => {
    const totalRevenue = serviceMetrics.transport.revenue + serviceMetrics.site.revenue + serviceMetrics.warehouse.revenue;
    const expenses = expenseStats?.paidAmount || 0;
    const profit = totalRevenue - expenses;
    return { totalRevenue, expenses, profit };
  }, [serviceMetrics, expenseStats]);

  return (
    <div className="bg-white rounded-lg border p-6">
      <h3 className="font-medium mb-4">Today's Metrics</h3>
      <div className="space-y-6">
        {/* Top Row - Service Metrics */}
        <div className="grid grid-cols-4 gap-6">
          <div>
            <div className="text-sm text-gray-500 mb-1">Staff Available</div>
            <div className="text-2xl font-semibold">{staffAvailable}</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Transport</div>
            <div className="text-2xl font-semibold">
              {formatCurrency(serviceMetrics.transport.revenue)}
            </div>
            <div className="text-sm text-gray-500">
              {serviceMetrics.transport.count} service{serviceMetrics.transport.count !== 1 ? "s" : ""}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Site Services</div>
            <div className="text-2xl font-semibold">
              {formatCurrency(serviceMetrics.site.revenue)}
            </div>
            <div className="text-sm text-gray-500">
              {serviceMetrics.site.count} service{serviceMetrics.site.count !== 1 ? "s" : ""}
            </div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Warehouse</div>
            <div className="text-2xl font-semibold">
              {formatCurrency(serviceMetrics.warehouse.revenue)}
            </div>
            <div className="text-sm text-gray-500">
              {serviceMetrics.warehouse.count} service{serviceMetrics.warehouse.count !== 1 ? "s" : ""}
            </div>
          </div>
        </div>

        {/* Bottom Row - Financial Summary */}
        <div className="grid grid-cols-4 gap-6 pt-4 border-t">
          <div>
            <div className="text-sm text-gray-500 mb-1">Staff Hours</div>
            <div className="text-2xl font-semibold">{serviceMetrics.totalHours.toFixed(1)}</div>
            <div className="text-sm text-gray-500">{serviceMetrics.totalServices} services</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Est. Revenue</div>
            <div className="text-2xl font-semibold text-green-600">
              {formatCurrency(financialMetrics.totalRevenue)}
            </div>
            <div className="text-sm text-gray-500">today's services</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Direct Expenses</div>
            <div className="text-2xl font-semibold text-red-600">
              {formatCurrency(financialMetrics.expenses)}
            </div>
            <div className="text-sm text-gray-500">paid out</div>
          </div>
          <div>
            <div className="text-sm text-gray-500 mb-1">Est. Profit</div>
            <div className={`text-2xl font-semibold ${financialMetrics.profit >= 0 ? "text-blue-600" : "text-red-600"}`}>
              {formatCurrency(financialMetrics.profit)}
            </div>
            <div className="text-sm text-gray-500">today</div>
          </div>
        </div>
      </div>
    </div>
  );
};